import { useState } from 'react'
import { router } from '@inertiajs/react'
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline'
import { FormSelect } from '@/components/ui/form'

// ─── Types ──────────────────────────────────────

interface Shipment {
  id: number
  tracking_number: string | null
  carrier_id: number | null
  carrier_name: string | null
  arrival_date: string | null
  incoming_ship_date: string | null
  date_routed: string | null
  count_pallets: number | null
  weight: string | null
  freight_invoice_uploaded: boolean | null
  receiving_details: string | null
}

interface ShipmentTabProps {
  vpoId: number
  shipments: Shipment[] | undefined
  carriers: { id: number; name: string }[]
  csrfToken: string
  onNotify: (msg: string) => void
}

// ─── ShipmentTab ────────────────────────────────

export default function ShipmentTab({
  vpoId, shipments, carriers, csrfToken, onNotify,
}: ShipmentTabProps) {
  const [adding, setAdding] = useState(false)
  const [tracking, setTracking] = useState('')
  const [carrier, setCarrier] = useState('')
  const [arrival, setArrival] = useState('')
  const [pallets, setPallets] = useState('')
  const [weight, setWeight] = useState('')

  const reload = () => router.reload({ only: ['shipments'] })

  const submit = async () => {
    if (!tracking) {
      onNotify('Tracking number is required.')
      return
    }
    const fd = new FormData()
    fd.append('tracking_number', tracking)
    if (carrier) fd.append('carrier_id', carrier)
    if (arrival) fd.append('arrival_date', arrival)
    if (pallets) fd.append('count_pallets', pallets)
    if (weight)  fd.append('weight', weight)

    const res = await fetch(`/vpo/create_shipment/${vpoId}`, {
      method: 'POST', body: fd, headers: { 'X-CSRF-Token': csrfToken },
    })
    if (res.ok || res.redirected) {
      onNotify('Shipment added.')
      setTracking(''); setCarrier(''); setArrival(''); setPallets(''); setWeight(''); setAdding(false)
      reload()
    } else {
      onNotify(`Failed to add shipment (${res.status}).`)
    }
  }

  const remove = async (id: number) => {
    if (!confirm('Delete this shipment?')) return
    const res = await fetch(`/vpo/delete_shipment/${id}`, {
      method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken, 'Accept': 'application/json' },
    })
    if (res.ok || res.redirected) { onNotify('Shipment deleted.'); reload() }
    else onNotify(`Failed to delete shipment (${res.status}).`)
  }

  return (
    <div className="space-y-4" data-testid="vpo-shipment-tab">
      <section data-testid="vpo-shipments-section">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Incoming Shipments</h3>
          <button
            type="button"
            onClick={() => setAdding(o => !o)}
            data-testid="vpo-add-shipment-toggle"
            className="inline-flex items-center gap-1 rounded-md border border-zinc-200 px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
          >
            <PlusIcon className="h-3 w-3" />
            {adding ? 'Cancel' : 'Add Shipment'}
          </button>
        </div>

        {adding && (
          <div className="mb-3 grid grid-cols-1 gap-2 rounded-lg border border-indigo-200 bg-indigo-50/30 p-3 dark:border-indigo-500/30 dark:bg-indigo-500/5 sm:grid-cols-6" data-testid="vpo-shipment-form">
            <input type="text" placeholder="Tracking # *" value={tracking} onChange={e => setTracking(e.target.value)}
              className="rounded-md border border-zinc-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800 dark:text-white sm:col-span-2" />
            <FormSelect value={carrier} onChange={setCarrier} placeholder="Carrier"
              options={carriers.map(c => ({ value: String(c.id), label: c.name }))} />
            <input type="date" value={arrival} onChange={e => setArrival(e.target.value)}
              className="rounded-md border border-zinc-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800 dark:text-white" />
            <input type="number" placeholder="Pallets" value={pallets} onChange={e => setPallets(e.target.value)}
              className="rounded-md border border-zinc-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800 dark:text-white" />
            <button type="button" onClick={submit} data-testid="vpo-shipment-submit"
              className="rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-700">
              Save
            </button>
          </div>
        )}

        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-zinc-200 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:border-zinc-700">
              <th className="py-1.5 pr-2">Tracking #</th>
              <th className="py-1.5 pr-2">Carrier</th>
              <th className="py-1.5 pr-2">Ship Date</th>
              <th className="py-1.5 pr-2">Arrival</th>
              <th className="py-1.5 pr-2">Pallets</th>
              <th className="py-1.5 pr-2">Weight</th>
              <th className="py-1.5 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {shipments === undefined ? (
              <tr><td colSpan={7} className="py-2 text-xs text-zinc-400" data-testid="vpo-shipments-loading">Loading shipments…</td></tr>
            ) : shipments.length === 0 ? (
              <tr><td colSpan={7} className="py-2 text-xs text-zinc-400">No shipments recorded.</td></tr>
            ) : (
              shipments.map(s => (
                <tr key={s.id} className="group border-b border-zinc-100 dark:border-zinc-800">
                  <td className="py-1.5 pr-2 font-medium">{s.tracking_number || '—'}</td>
                  <td className="py-1.5 pr-2">{s.carrier_name || '—'}</td>
                  <td className="py-1.5 pr-2 text-zinc-600 dark:text-zinc-400">{s.incoming_ship_date || '—'}</td>
                  <td className="py-1.5 pr-2 text-zinc-600 dark:text-zinc-400">{s.arrival_date || '—'}</td>
                  <td className="py-1.5 pr-2 tabular-nums">{s.count_pallets ?? '—'}</td>
                  <td className="py-1.5 pr-2 tabular-nums">{s.weight || '—'}</td>
                  <td className="py-1.5">
                    <button
                      onClick={() => remove(s.id)}
                      data-testid={`vpo-shipment-delete-${s.id}`}
                      className="hidden rounded-md border border-zinc-200 p-0.5 text-zinc-400 hover:border-red-200 hover:text-red-500 group-hover:inline dark:border-zinc-700"
                    >
                      <TrashIcon className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </div>
  )
}
